import type { AttributeDef, Product, ProductAttributes } from '../types';
import { parseNumericValue } from './parseNumericValue';

/**
 * Vista tipada en memoria: resuelve cada spec de `features` contra el registro
 * (match por label) y extrae los valores de las defs filtrables (enum o
 * number), normalizados (enum a lowercase, number a su valor parseado).
 * Genérico — agregar una característica filtrable en el registro no requiere
 * tocar este código.
 */
export function deriveAttributes(
  features: Product['features'],
  defs: AttributeDef[],
): ProductAttributes {
  const entries = [...(features?.main ?? []), ...(features?.other ?? [])];
  const attributes: ProductAttributes = {};
  const defsByLabel = new Map(defs.map(d => [d.label, d]));

  for (const entry of entries) {
    if (!entry.header || !entry.body) continue;

    const def = defsByLabel.get(entry.header);
    if (!def || !def.filterable) continue;

    if (def.type === 'number') {
      const n = parseNumericValue(entry.body);
      if (n !== undefined) attributes[def.code] = [String(n)];
      continue;
    }

    if (def.type !== 'enum' || !def.options) continue;

    const allowed = new Set(def.options.map(o => o.toLowerCase()));
    const raw = def.multiple ? entry.body.split(',') : [entry.body];
    const values = raw.reduce<string[]>((acc, v) => {
      const normalized = v.trim().toLowerCase();
      if (allowed.has(normalized)) acc.push(normalized);
      return acc;
    }, []);

    if (values.length > 0) {
      attributes[def.code] = [...new Set([...(attributes[def.code] ?? []), ...values])];
    }
  }

  return attributes;
}
